import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import AgentService from 'api/services/AgentService';
import { RootState } from 'store/store';
import { selectCallouts } from './companySetting';
import { ACTIVITIES } from 'types';
import { parseDateToISO } from 'pages/ActivityTracker/items/DetailedPopup/helpers';

export const fetchAgentStatisticByTimeframe = createAsyncThunk<
    any,
    { start: Date; end: Date; activities: Array<string>; agentId: string },
    { state: RootState }
>('fetchAgentStatisticByTimeframe', async (params, { getState }) => {
    const companyId = getState().userState.me.data.company.id;
    const response = await AgentService.timeframe({ companyId, ...params });
    return response.data;
});

export const fetchAgentStatistic = createAsyncThunk<
    any,
    { start: Date; end: Date; agentId: string },
    { state: RootState }
>('fetchAgentStatistic', async (params, { getState }) => {
    const companyId = getState().userState.me.data.company.id;
    const response = await AgentService.summary({ companyId, ...params });
    return response.data;
});

export const fetchDetailed = createAsyncThunk<any, { start: Date; end: Date; agentId: string }, { state: RootState }>(
    'fetchDetailed',
    async (params, { getState }) => {
        const companyId = getState().userState.me.data.company.id;
        const response = await AgentService.detailed({ companyId, ...params });
        return response.data;
    },
);

type TTeamsState = {
    agentStatistic: {
        data:
            | {}
            | {
                  kpis: {};
                  detailed: {};
                  request: {
                      start: Date;
                      end: Date;
                  };
                  user: {
                      email: string;
                      fullName: string;
                  };
                  computed: {
                      calls: number;
                      earlyHangUps: number;
                      sms: number;
                      pickUps: number;
                      convos: number;
                      offers: number;
                      dq: number;
                      sets: number;
                      triage: number;
                      workTime: string;
                  };
                  timeframes: {};
              };
        isLoading: boolean;
    };
    selectedUser: { label: string; value: string };
    detailed: {
        data: any;
        isLoading: boolean;
    };
};

const initialState: TTeamsState = {
    agentStatistic: {
        data: {
            detailed: {},
            computed: {},
            kpis: {},
        },
        isLoading: false,
    },
    selectedUser: {} as { label: string; value: string },
    detailed: {
        data: {
            computed: {},
            timeframes: {
                calls: [],
                convos: [],
                dds: [],
                dq: [],
                earlyHangUps: [],
                offers: [],
                pickUps: [],
                sets: [],
                sms: [],
                triage: [],
                uniqueCalls: [],
            },
        } as any,
        isLoading: false,
    },
};

export const agentSlice = createSlice({
    name: 'agent',
    initialState,
    reducers: {
        setSelectedUser(state, action) {
            state.selectedUser = action.payload;
        },
        resetStatistic: () => initialState,
    },
    extraReducers: builder => {
        builder
            .addCase(fetchAgentStatistic.pending, state => {
                state.agentStatistic.isLoading = true;
            })
            .addCase(fetchAgentStatistic.fulfilled, (state, action) => {
                state.agentStatistic.data = action.payload;
                state.agentStatistic.isLoading = false;
            });
        builder
            .addCase(fetchDetailed.pending, state => {
                state.detailed.isLoading = true;
            })
            .addCase(fetchDetailed.fulfilled, (state, action) => {
                state.detailed.data = action.payload;
                state.detailed.isLoading = false;
            });
    },
});

export const selectAgentStatistic = createSelector(
    [(state: RootState) => state.agentState.agentStatistic],
    data => data.data,
);
export const selectAgentStatisticComputed = createSelector([selectAgentStatistic], data => data.computed || {});

export const selectAgentStatisticData = createSelector([selectAgentStatistic, selectCallouts], (data, callouts) => {
    const { calls, dds, uniqueCalls, earlyHangUps, sms, pickUps, convos, offers, dq, sets, triage, efficiency } =
        data.computed;
    const { pickUpRate, uniquePickUpRate, avgDU, convoRate, offerRate, dqRate, setRate } = efficiency || {};
    const kpis = data.kpis;

    return [
        { title: callouts[ACTIVITIES.calls], value: calls },
        { title: callouts[ACTIVITIES.dds], value: dds },
        { title: callouts[ACTIVITIES.uniqueCalls], value: uniqueCalls },
        { title: callouts[ACTIVITIES.earlyHangUps], value: earlyHangUps },
        { title: callouts[ACTIVITIES.sms], value: sms },
        {
            title: callouts[ACTIVITIES.pickUps],
            value: pickUps,
            rate: {
                positive: pickUpRate >= kpis.pickUpRate,
                value: pickUpRate,
            },
        },
        {
            title: callouts[ACTIVITIES.convos],
            value: convos,
            rate: {
                positive: convoRate >= kpis.convoRate,
                value: convoRate,
            },
        },
        {
            title: callouts[ACTIVITIES.offers],
            value: offers,
            rate: {
                positive: offerRate >= kpis.offerRate,
                value: offerRate,
            },
        },
        {
            title: callouts[ACTIVITIES.dq],
            value: dq,
            rate: {
                positive: dqRate >= kpis.dqRate,
                value: dqRate,
            },
        },
        {
            title: callouts[ACTIVITIES.sets],
            value: sets,
            rate: {
                positive: setRate >= kpis.setRate,
                value: setRate,
            },
        },
        {
            title: callouts[ACTIVITIES.triage],
            value: triage,
        },
    ];
});

export const selectAgentStatisticKpis = createSelector([selectAgentStatistic], data => data.kpis || {});
export const selectAgentStatisticDetailed = createSelector([selectAgentStatistic], data =>
    Object.entries(data.detailed as object)
        .sort(([a], [b]) => parseDateToISO(a).getTime() - parseDateToISO(b).getTime())
        .map(([key, value]) => ({
            date: key,
            ...value,
        })),
);

export const selectAgentStatisticIsLoading = createSelector(
    [(state: RootState) => state.agentState.agentStatistic],
    data => data.isLoading,
);

export const selectDetailed = createSelector([(state: RootState) => state.agentState.detailed], data => data.data);
export const selectDetailedTimeframes = createSelector([selectDetailed], data => data.timeframes);

export const selectDetailedIsLoading = createSelector(
    [(state: RootState) => state.agentState.detailed],
    data => data.isLoading,
);

export const selectSelectedUser = createSelector([(state: RootState) => state.agentState], data => data.selectedUser);

export const { setSelectedUser, resetStatistic } = agentSlice.actions;

export default agentSlice.reducer;
